import { useState, useEffect, useMemo, useRef } from 'react';
import API from '@/lib/api';
import { Plus, Trash, PencilSimple, X, Envelope, Eye, Sparkle, Code, FloppyDisk, Translate } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TYPES = [
  { value: 'factuur', label: 'Factuur', kleur: '#2563EB' },
  { value: 'offerte', label: 'Offerte', kleur: '#059669' },
  { value: 'herinnering', label: '1ste herinnering', kleur: '#D97706' },
  { value: 'aanmaning', label: 'Aanmaning (dringend)', kleur: '#DC2626' },
  { value: 'custom', label: 'Custom', kleur: '#6B7280' },
];

function typeInfo(v) { return TYPES.find(t => t.value === v) || TYPES[TYPES.length - 1]; }

export default function EmailTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [variabelen, setVariabelen] = useState({});
  const [editing, setEditing] = useState(null); // template in edit-mode
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBusy, setSavingBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [msg, setMsg] = useState('');
  const bodyRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      API.get('/email-templates').then(r => r.data).catch(() => []),
      API.get('/email-templates/variabelen').then(r => r.data).catch(() => ({})),
    ]).then(([t, v]) => {
      setTemplates(t || []);
      setVariabelen(v || {});
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const zaaiStandaard = async () => {
    const r = await API.post('/email-templates/zaai-standaard');
    setMsg(r.data.bericht || 'Klaar');
    load();
    setTimeout(() => setMsg(''), 3000);
  };

  const nieuwTemplate = () => setEditing({ naam: '', type: 'custom', onderwerp: '', body_html: '', body_tekst: '', taal: 'nl', is_default: false, _nieuw: true });
  const bewerken = (t) => setEditing({ ...t });
  const verwijderen = async (t) => {
    if (!window.confirm(`Template "${t.naam}" verwijderen?`)) return;
    await API.delete(`/email-templates/${t.id}`);
    load();
  };

  const opslaan = async () => {
    if (!editing) return;
    setSavingBusy(true);
    try {
      if (editing._nieuw) {
        await API.post('/email-templates', editing);
      } else {
        await API.put(`/email-templates/${editing.id}`, editing);
      }
      setEditing(null);
      setPreview(null);
      setMsg('Template opgeslagen');
      load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.detail || e.message));
    } finally { setSavingBusy(false); }
  };

  const vertaalTemplate = async () => {
    if (!editing || editing._nieuw || !editing.id) {
      toast.error('Bewaar eerst de template voor je hem kan vertalen');
      return;
    }
    const TAAL_LABELS = { nl: 'Nederlands', fr: 'Français', en: 'English', de: 'Deutsch' };
    const src = editing.taal || 'nl';
    const targets = ['nl', 'fr', 'en', 'de'].filter((t) => t !== src);
    if (!window.confirm(
      `Vertaal "${editing.naam}" van ${TAAL_LABELS[src]} naar ${targets.map((t) => TAAL_LABELS[t]).join(', ')}?\n\n` +
      `Dit maakt 3 nieuwe templates aan met "(FR)/(EN)/(DE)" suffix — bestaande templates met dezelfde naam worden overschreven.`
    )) return;
    setTranslating(true);
    try {
      await API.put(`/email-templates/${editing.id}`, editing);
      const r = await API.post(`/email-templates/${editing.id}/translate`, { targets, save_as_new: true });
      const okLangs = (r.data?.targets || []).filter((t) => (r.data?.translations || {})[t]);
      if (okLangs.length === 0) {
        toast.error('Vertaling mislukt');
      } else {
        toast.success(`${okLangs.length} nieuwe templates aangemaakt: ${okLangs.map((t) => TAAL_LABELS[t]).join(', ')}`);
        load();
        setEditing(null);
        setPreview(null);
      }
    } catch (e) {
      toast.error('Vertaling mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setTranslating(false);
    }
  };

  const toonPreview = async () => {
    if (!editing) return;
    // Eerst opslaan als het een bestaande is, dan preview roepen. Voor nieuw: doen we een virtuele render client-side via voorbeelddata.
    try {
      if (editing.id && !editing._nieuw) {
        // Eerst huidige wijzigingen opslaan zodat preview de nieuwste versie toont
        await API.put(`/email-templates/${editing.id}`, editing);
        const r = await API.post(`/email-templates/${editing.id}/preview`, {});
        setPreview(r.data);
      } else {
        // Voor nieuwe: client-side preview met voorbeeld-context
        const voorbeeldCtx = {
          bedrijf: { naam: 'Alphaprofiles BV', email: 'info@alphaprofiles.be', tel: '+32 2 123 45 67', iban: 'BE00 1234 5678 9012' },
          klant: { naam: 'Jan Peeters', email: 'jan@peeters.be', tav: 'Dhr. Peeters', btw: 'BE0123.456.789', adres: 'Hoofdstraat 12, 2000 Antwerpen' },
          document: { id: 'F-2026-0023', type: 'Factuur', datum: '2026-04-15', verval: '2026-05-15', totaal: '€ 1.234,56', saldo: '€ 1.234,56', mededeling: '+++012/3456/78901+++', dagen_te_laat: '0' },
          datum: { vandaag: new Date().toLocaleDateString('nl-BE'), jaar: String(new Date().getFullYear()) },
        };
        const renderLocal = (s) => (s || '').replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, p) => {
          const delen = p.split('.');
          let v = voorbeeldCtx;
          for (const d of delen) {
            if (v && typeof v === 'object' && d in v) v = v[d]; else return '';
          }
          return v == null ? '' : String(v);
        });
        setPreview({
          onderwerp: renderLocal(editing.onderwerp),
          body_html: renderLocal(editing.body_html),
          body_tekst: renderLocal(editing.body_tekst),
          context: voorbeeldCtx,
        });
      }
    } catch (e) {
      alert('Preview mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Variabele invoegen op cursorpositie (body-veld)
  const voegVariabeleIn = (sleutel) => {
    if (!bodyRef.current) return;
    const ta = bodyRef.current;
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const token = `{{${sleutel}}}`;
    const nieuw = ta.value.substring(0, start) + token + ta.value.substring(end);
    setEditing(e => ({ ...e, body_html: nieuw }));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const variabelenAanwezig = useMemo(() => {
    if (!editing) return [];
    const combined = `${editing.onderwerp || ''} ${editing.body_html || ''} ${editing.body_tekst || ''}`;
    const matches = combined.match(/\{\{\s*[a-zA-Z0-9_.]+\s*\}\}/g) || [];
    return Array.from(new Set(matches));
  }, [editing]);

  return (
    <div data-testid="email-templates-panel">
      {/* Header + acties */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">E-mail templates</h2>
          <p className="text-sm text-gray-500">Herbruikbare templates met variabelen zoals <code className="text-[11px] bg-gray-100 px-1 rounded">{'{{klant.naam}}'}</code> — automatisch ingevuld bij verzenden.</p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && !loading && (
            <button onClick={zaaiStandaard} className="btn btn-secondary inline-flex items-center gap-1.5 font-bold" data-testid="zaai-standaard-btn">
              <Sparkle size={14} weight="bold" /> Start met 4 standaard-templates
            </button>
          )}
          <button onClick={nieuwTemplate} className="btn btn-primary inline-flex items-center gap-1.5 font-bold" data-testid="nieuwe-template-btn">
            <Plus size={14} weight="bold" /> Nieuwe template
          </button>
        </div>
      </div>

      {msg && <div className="mb-4 p-3 border border-green-200 bg-green-50 text-green-800 text-sm rounded-lg" data-testid="template-msg">{msg}</div>}

      {/* Templates-lijst */}
      {loading ? (
        <div className="text-center py-8 text-gray-400 italic">Laden...</div>
      ) : templates.length === 0 && !editing ? (
        <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 text-center" style={{ border: '1px dashed rgba(99,102,241,0.3)' }}>
          <Envelope size={40} weight="duotone" className="mx-auto text-indigo-400 mb-3" />
          <div className="text-[14px] font-bold text-gray-900 mb-1">Nog geen e-mail templates</div>
          <div className="text-[12px] text-gray-500 mb-4">Klik "Start met 4 standaard-templates" om direct professionele templates (factuur, offerte, herinnering, aanmaning) te laden.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="templates-list">
          {templates.map(t => {
            const info = typeInfo(t.type);
            return (
              <div key={t.id} className="bg-white rounded-2xl overflow-hidden flex items-center gap-3 px-3 py-3" style={{ border: '1px solid rgba(0,0,0,0.06)' }} data-testid={`template-card-${t.id}`}>
                <div className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: 10, background: `${info.kleur}15`, border: `1px solid ${info.kleur}30` }}>
                  <Envelope size={20} weight="bold" color={info.kleur} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-bold text-gray-900">{t.naam}</span>
                    {t.is_default && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">DEFAULT</span>}
                  </div>
                  <div className="text-[12px] text-gray-500 truncate mt-0.5">{t.onderwerp || <span className="italic">Geen onderwerp</span>}</div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider" style={{ background: `${info.kleur}15`, color: info.kleur, border: `1px solid ${info.kleur}30` }}>{info.label}</span>
                <div className="flex items-center gap-1">
                  <button className="flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors" style={{ width: 36, height: 36 }} onClick={() => bewerken(t)} data-testid={`edit-template-${t.id}`}>
                    <PencilSimple size={16} className="text-gray-600" />
                  </button>
                  <button className="flex items-center justify-center hover:bg-red-50 rounded-lg transition-colors" style={{ width: 36, height: 36 }} onClick={() => verwijderen(t)} data-testid={`del-template-${t.id}`}>
                    <Trash size={15} className="text-red-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit-modal */}
      {editing && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setEditing(null); setPreview(null); }}>
          <div className="w-full max-w-6xl bg-white shadow-2xl rounded-2xl overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 40px)' }} onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between" style={{ background: '#F5F5F7' }}>
              <span className="text-[14px] font-bold text-gray-900">{editing._nieuw ? 'Nieuwe template' : `Template bewerken — ${editing.naam}`}</span>
              <button className="text-gray-400 hover:text-gray-700" onClick={() => { setEditing(null); setPreview(null); }} data-testid="tpl-close-btn"><X size={18} /></button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Linker kolom: variabelen */}
              <div className="w-56 border-r border-gray-100 bg-gray-50 overflow-y-auto shrink-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-3 py-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Variabelen</div>
                  <div className="text-[10px] text-gray-400 mb-3">Klik om in body in te voegen</div>
                  {Object.entries(variabelen).map(([cat, items]) => (
                    <div key={cat} className="mb-3">
                      <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">{cat}</div>
                      {items.map(v => (
                        <button
                          key={v.sleutel}
                          onClick={() => voegVariabeleIn(v.sleutel)}
                          className="w-full text-left px-2 py-1.5 mb-1 bg-white hover:bg-indigo-50 border border-gray-200 rounded text-[11px] transition-colors"
                          title={`Voorbeeld: ${v.voorbeeld}`}
                          data-testid={`var-${v.sleutel}`}
                        >
                          <div className="font-mono text-indigo-700">{`{{${v.sleutel}}}`}</div>
                          <div className="text-gray-500 truncate">{v.label}</div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Midden: editor */}
              <div className="flex-1 overflow-y-auto p-5" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Template-naam</label>
                    <input className="field-input" value={editing.naam} onChange={e => setEditing(p => ({ ...p, naam: e.target.value }))} data-testid="tpl-naam" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Type</label>
                    <select className="field-input" value={editing.type} onChange={e => setEditing(p => ({ ...p, type: e.target.value }))} data-testid="tpl-type">
                      {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Onderwerp</label>
                  <input
                    className="field-input"
                    value={editing.onderwerp}
                    onChange={e => setEditing(p => ({ ...p, onderwerp: e.target.value }))}
                    placeholder="bv. {{bedrijf.naam}} — Factuur {{document.id}}"
                    data-testid="tpl-onderwerp"
                  />
                </div>
                <div className="mb-3">
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Body (HTML)</label>
                  <textarea
                    ref={bodyRef}
                    className="field-input font-mono text-[12px]"
                    rows={14}
                    value={editing.body_html}
                    onChange={e => setEditing(p => ({ ...p, body_html: e.target.value }))}
                    placeholder="<p>Geachte {{klant.tav}},</p>..."
                    data-testid="tpl-body-html"
                  />
                </div>
                <label className="inline-flex items-center gap-2 text-[12px] text-gray-700">
                  <input type="checkbox" checked={!!editing.is_default} onChange={e => setEditing(p => ({ ...p, is_default: e.target.checked }))} data-testid="tpl-default" />
                  <span>Standaard-template voor dit type (<strong>{typeInfo(editing.type).label}</strong>) — wordt automatisch gebruikt bij verzenden</span>
                </label>

                {/* Chips van gebruikte variabelen */}
                {variabelenAanwezig.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <span className="text-[10px] text-gray-500 mr-1 py-1">Gebruikt:</span>
                    {variabelenAanwezig.map(v => (
                      <span key={v} className="text-[10px] font-mono px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded">{v}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Rechts: preview */}
              <div className="w-[420px] border-l border-gray-100 bg-gray-50 overflow-y-auto shrink-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5"><Eye size={12} weight="bold" /> Live preview</span>
                  <button onClick={toonPreview} className="text-[11px] font-bold text-[#002FA7] hover:underline" data-testid="tpl-preview-btn">Vernieuwen</button>
                </div>
                {preview ? (
                  <div className="p-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
                      <div className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">Onderwerp</div>
                      <div className="text-[13px] font-bold text-gray-900">{preview.onderwerp}</div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-[9px] text-gray-400 uppercase tracking-wider mb-2">Bericht</div>
                      <div className="text-[12px] text-gray-800 prose prose-sm max-w-none" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: preview.body_html || '<em>Geen HTML-body</em>' }} />
                    </div>
                    <div className="mt-3 text-[10px] text-gray-400 italic">Preview met voorbeelddata (Jan Peeters — Factuur F-2026-0023)</div>
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <Code size={32} weight="duotone" className="mx-auto text-gray-300 mb-2" />
                    <div className="text-[12px] text-gray-500">Klik "Vernieuwen" om de preview met voorbeelddata te tonen</div>
                    <button onClick={toonPreview} className="mt-3 btn btn-secondary btn-sm" data-testid="tpl-preview-init-btn">Genereer preview</button>
                  </div>
                )}
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
              {!editing._nieuw && editing.id && (
                <button
                  className="btn btn-secondary inline-flex items-center gap-1.5 mr-auto"
                  onClick={vertaalTemplate}
                  disabled={translating}
                  title="Vertaal deze template via AI naar de overige 3 talen — maakt 3 nieuwe templates aan"
                  data-testid="tpl-translate-btn"
                  style={{ background: '#2563EB', color: 'white' }}
                >
                  <Translate size={14} weight="bold" />
                  {translating ? 'Bezig...' : 'Vertaal naar andere talen'}
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => { setEditing(null); setPreview(null); }}>Annuleren</button>
              <button className="btn btn-primary inline-flex items-center gap-1.5 font-bold" onClick={opslaan} disabled={savingBusy} data-testid="tpl-save-btn">
                <FloppyDisk size={14} weight="bold" /> {savingBusy ? 'Bezig...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
